// src/features/tuition/services/studentsService.js
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp 
} from "firebase/firestore";
import { db } from "../../../firebase/config";

export const fetchStudents = async (userId) => {
  if (!userId) throw new Error("User ID is required");
  
  try {
    const studentsRef = collection(db, "tuitionStudents");
    const q = query(
      studentsRef,
      where("tutorId", "==", userId),
      orderBy("createdAt", "desc")
    );
    
    const querySnapshot = await getDocs(q);
    const students = [];
    
    querySnapshot.forEach((doc) => {
      students.push({
        id: doc.id,
        ...doc.data(),
        // Convert Firestore timestamps to string dates for UI
        startDate: doc.data().startDate?.toDate().toISOString().split('T')[0] || null,
        endDate: doc.data().endDate?.toDate().toISOString().split('T')[0] || null
      });
    });
    
    return students;
  } catch (error) {
    console.error("Error fetching students:", error);
    throw error;
  }
};

export const getStudent = async (studentId) => {
  if (!studentId) throw new Error("Student ID is required");
  
  try {
    const studentRef = doc(db, "tuitionStudents", studentId);
    const studentDoc = await getDoc(studentRef);
    
    if (!studentDoc.exists()) {
      throw new Error("Student not found");
    }
    
    const studentData = studentDoc.data();
    return {
      id: studentDoc.id,
      ...studentData,
      // Convert Firestore timestamps to string dates for UI
      startDate: studentData.startDate?.toDate().toISOString().split('T')[0] || null,
      endDate: studentData.endDate?.toDate().toISOString().split('T')[0] || null
    };
  } catch (error) {
    console.error("Error getting student:", error);
    throw error;
  }
};

export const createStudent = async (userId, studentData) => {
  if (!userId) throw new Error("User ID is required");
  
  try {
    // Prepare data for Firestore
    const newStudent = {
      ...studentData,
      tutorId: userId,
      status: 'active',
      startDate: new Date(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    const studentsRef = collection(db, "tuitionStudents");
    const docRef = await addDoc(studentsRef, newStudent);
    
    // Get the newly created student with ID
    const studentDoc = await getDoc(docRef);
    return {
      id: studentDoc.id,
      ...studentDoc.data(),
      startDate: newStudent.startDate.toISOString().split('T')[0]
    };
  } catch (error) {
    console.error("Error creating student:", error);
    throw error;
  }
};

export const updateStudent = async (studentId, studentData) => {
  if (!studentId) throw new Error("Student ID is required");
  
  try {
    const studentRef = doc(db, "tuitionStudents", studentId);
    
    // Remove fields that shouldn't be updated directly
    const { id, createdAt, tutorId, ...updateData } = studentData;
    
    // Update the document
    await updateDoc(studentRef, {
      ...updateData,
      updatedAt: serverTimestamp()
    });
    
    // Get the updated student
    const updatedStudentDoc = await getDoc(studentRef);
    return {
      id: updatedStudentDoc.id,
      ...updatedStudentDoc.data()
    };
  } catch (error) {
    console.error("Error updating student:", error);
    throw error;
  }
};

export const deleteStudent = async (studentId) => {
  if (!studentId) throw new Error("Student ID is required");
  
  try {
    await deleteDoc(doc(db, "tuitionStudents", studentId));
    return { success: true };
  } catch (error) {
    console.error("Error deleting student:", error);
    throw error;
  }
};

export const updateStudentStatus = async (studentId, status) => {
  if (!studentId) throw new Error("Student ID is required");
  
  try {
    const studentRef = doc(db, "tuitionStudents", studentId);
    
    const updateData = {
      status,
      updatedAt: serverTimestamp()
    };
    
    // If status is 'inactive', add end date
    if (status === 'inactive') {
      updateData.endDate = new Date();
    }
    
    await updateDoc(studentRef, updateData);
    
    return { success: true, id: studentId, status };
  } catch (error) {
    console.error("Error updating student status:", error);
    throw error;
  }
};

// Additional functionality for notes, sessions, and payments

export const addStudentNote = async (studentId, note) => {
  if (!studentId) throw new Error("Student ID is required");
  
  try {
    const notesRef = collection(db, "tuitionStudents", studentId, "notes");
    const noteData = {
      text: note,
      createdAt: serverTimestamp()
    };
    
    const docRef = await addDoc(notesRef, noteData);
    const noteDoc = await getDoc(docRef);
    
    return {
      id: noteDoc.id,
      ...noteDoc.data(),
      createdAt: new Date().toISOString() // For immediate UI display
    };
  } catch (error) {
    console.error("Error adding student note:", error);
    throw error;
  }
};

export const fetchStudentNotes = async (studentId) => {
  if (!studentId) throw new Error("Student ID is required");
  
  try {
    const notesRef = collection(db, "tuitionStudents", studentId, "notes");
    const q = query(notesRef, orderBy("createdAt", "desc"));
    
    const querySnapshot = await getDocs(q);
    const notes = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      notes.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate().toISOString() || null
      });
    });
    
    return notes;
  } catch (error) {
    console.error("Error fetching student notes:", error);
    throw error;
  }
};

export const fetchSessionsByStudent = async (studentId) => {
  if (!studentId) throw new Error("Student ID is required");
  
  try {
    const sessionsRef = collection(db, "tuitionSessions");
    const q = query(
      sessionsRef,
      where("studentId", "==", studentId),
      orderBy("date", "desc")
    );
    
    const querySnapshot = await getDocs(q);
    const sessions = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      sessions.push({
        id: doc.id,
        ...data,
        date: data.date?.toDate().toISOString().split('T')[0] || null
      });
    });
    
    return sessions;
  } catch (error) {
    console.error("Error fetching sessions for student:", error);
    throw error;
  }
};

export const fetchPaymentsByStudent = async (studentId) => {
  if (!studentId) throw new Error("Student ID is required");
  
  try {
    const paymentsRef = collection(db, "tuitionPayments");
    const q = query(
      paymentsRef,
      where("studentId", "==", studentId),
      orderBy("date", "desc")
    );
    
    const querySnapshot = await getDocs(q);
    const payments = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      payments.push({
        id: doc.id,
        ...data,
        date: data.date?.toDate().toISOString().split('T')[0] || null
      });
    });
    
    return payments;
  } catch (error) {
    console.error("Error fetching payments for student:", error);
    throw error;
  }
};